import { Button, Col, Container, Grid, Row, Typography } from 'gu-ui-kit'
import uniq from 'lodash/uniq'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import useQueryParams from 'utils/hooks/useQueryParams'
import formStyles from 'modules/Forms/common-form.module.scss'
import styles from 'modules/Oauth/oauth.module.scss'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { notificationShow } from 'modules/Notifications/actions'

const AVAILABLE_SCOPES = [
  'user_info',
  'user_wallets',
  'user_accounts',
  'user_partner_transfers',
  'user_balance',
  'account_info',
  'transfer_money',
  'financial_operations',
  'targetmail_token',
  'statistics',
  'withdrawal',
  'system_access',
]

const Oauth = () => {
  const pathGen = useGeneratePathI18n()
  const [name, setName] = useState()
  const [oauthUnavailable, setOauthUnavailable] = useState(false)
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { query: { client_id = null, scope = '', response_type = '', redirect_uri = '' } = {} } =
    useQueryParams()

  const scopes = useMemo(
    () => uniq(scope.split(' ')).filter((val) => AVAILABLE_SCOPES.includes(val)),
    [scope],
  )

  useEffect(() => {
    if (!client_id) {
      setOauthUnavailable(true)
      return
    }

    new Promise((resolve, reject) => {
      dispatch({
        type: 'OAUTH/FETCH_APP_DATA',
        payload: { params: { client_id } },
        meta: { resolve, reject },
      })
    })
      .then((resp) => {
        setName(resp.data.name)
      })
      .catch(() => setOauthUnavailable(true))
  }, [])

  const tryAuth = useCallback(
    () =>
      new Promise((resolve, reject) => {
        dispatch({
          type: 'OAUTH/TRY_AUTHENTICATE',
          payload: {
            data: {
              client_id,
              scope: scopes.join(' '),
              response_type,
              redirect_uri,
            },
          },
          meta: { resolve, reject },
        })
      }).then((resp) => (global.location = resp.data.location)),
    [client_id, scopes, response_type, redirect_uri],
  )

  const onDisallow = useCallback(() => {
    if (!oauthUnavailable) {
      dispatch(
        notificationShow({
          type: 'error',
          message: `oauth::oauth.notifications.errors.disallow_by_user`,
        }),
      )
    }

    history.push(pathGen(ROUTES.SERVICES))
  }, [redirect_uri, oauthUnavailable])

  return (
    <>
      <Helmet defer={false}>
        <title>{t('common::nav_menu.oauth_auth', { name })}</title>
      </Helmet>
      <Grid>
        <Row justifyContent="center">
          <Col sm="auto">
            <div className={formStyles['formContainer']}>
              <div className={formStyles['formContainer__inner']}>
                <Typography
                  variant={'Header-60'}
                  className={'margin-bottom-16'}
                  component={'h1'}
                >
                  {t(`oauth::oauth.title`, {
                    context: oauthUnavailable ? 'error' : '',
                  })}
                </Typography>
                {!oauthUnavailable ? (
                  <>
                    <Typography
                      variant={'Body-15'}
                      color="Label-Secondary"
                      component={'p'}
                    >
                      {t(`oauth::oauth.description`, { name })}
                    </Typography>
                    <Typography
                      variant={'Subtitle-10'}
                      component={'p'}
                    >
                      {t(`oauth::oauth.scopes.description`)}:
                    </Typography>
                    <Typography
                      variant={'Body-15'}
                      color="Label-Secondary"
                      component={'ul'}
                      className={styles['scopes-list']}
                    >
                      {[].concat(scopes).map((sc) => (
                        <li key={'sc-' + sc}>{t(`oauth::oauth.scopes.options.${sc}`)}</li>
                      ))}
                    </Typography>
                    <div className={formStyles['formControl__action-buttons']}>
                      <Button
                        onClick={() => onDisallow()}
                        variant="ghost"
                        grey
                        size="l"
                        type="button"
                      >
                        {t('oauth::oauth.buttons.disallow')}
                      </Button>

                      <Button
                        noWrap
                        disabled={!name}
                        size="l"
                        variant="primary"
                        type="submit"
                        bgColor="Label-GU-Primary"
                        onClick={() => tryAuth()}
                      >
                        {t('oauth::oauth.buttons.allow')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Container
                      display={'flex'}
                      flexAlign={'flex-end'}
                      flexJustify={'flex-end'}
                    >
                      <Button
                        onClick={() => onDisallow()}
                        size="l"
                        variant="primary"
                        type="submit"
                        bgColor="Label-GU-Primary"
                      >
                        {t('oauth::oauth.buttons.cancel')}
                      </Button>
                    </Container>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Grid>
    </>
  )
}

export default Oauth
